import cn from 'classnames';
import { useIntl } from 'react-intl';
import { Outlet, useParams } from 'react-router-dom';
import { useFeesContext } from 'sonata-platform-components';
import { SimplifiedPricingModelEnum } from 'sonata-static-storage/lib/schemas/user/userDataFee.schema';

import { FeesBar } from '@/components/common/FeesBar/FeesBar';
import { Main } from '@/components/common/Main';
import PromptModal from '@/components/common/PromptModal/PromptModal';
import { SubHeader } from '@/components/common/SubHeader/SubHeader';
import { useNavigationGuard } from '@/context/NavigationGuardProvider';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

type Props = {
  entityType: 'lineItem' | 'insertionOrder';
  hasUnsavedChanges: boolean;
  pricingModel: SimplifiedPricingModelEnum;
};

export function WizardLayout({ entityType, hasUnsavedChanges, pricingModel }: Props) {
  const { formatMessage } = useIntl();
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const { totalFees } = useFeesContext();
  const { id } = useParams();
  const { cancel, isNavigationBlocked, proceed } = useNavigationGuard(hasUnsavedChanges);

  const mainClassName = cn('mainLayout mainLayout__wizard', {
    'mainLayout__wizard--withFeesBar': totalFees > 0
  });

  return (
    <>
      <SubHeader entity={entityType} />
      <FeesBar currencyInfo={currencyInfo} pricingModel={pricingModel} />
      <Main className={mainClassName}>
        <Outlet
          context={{
            id
          }}
        />
      </Main>
      {isNavigationBlocked && (
        <PromptModal
          cancelCallback={cancel}
          confirmCallback={proceed}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Discard and exit',
                id: 'wizardLayout.navigationGuard.confirmButton'
              })
            },
            description: formatMessage({
              defaultMessage: 'You will lose all unsaved progress if you leave this page.',
              id: 'wizardLayout.navigationGuard.description'
            }),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to leave?',
              id: 'wizardLayout.navigationGuard.title'
            })
          }}
        />
      )}
    </>
  );
}
